import { useCallback, useLayoutEffect, useState } from 'react';
import type { PageSize } from '../utils/pageFit';

/** Measures usable content space, excluding padding, borders and scrollbars. */
export function useViewportSize<T extends HTMLElement = HTMLDivElement>() {
  const [element, setElement] = useState<T | null>(null);
  const [size, setSize] = useState<PageSize>({ width: 0, height: 0 });
  const ref = useCallback((node: T | null) => setElement(node), []);
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => {
      const style = getComputedStyle(element);
      const px = (value: string) => parseFloat(value) || 0;
      const width = Math.max(0, element.clientWidth - px(style.paddingLeft) - px(style.paddingRight));
      const height = Math.max(0, element.clientHeight - px(style.paddingTop) - px(style.paddingBottom));
      setSize((old) => old.width === width && old.height === height ? old : { width, height });
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(element);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [element]);
  return { ref, size };
}
